import { useEffect, useRef, useState } from "react";
import { Row, Col, Empty, Spin, Divider, Button, Tooltip, Card, Input } from "antd";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

import { PillPagination } from "../../components/PillPagination";

import { ProjectCard } from "./partials/ProjectCard";
import ProjectFilter from "../../components/ProjectFilter";
import StatusCards from "./partials/StatusCards";
import { BASE_PROJECTS_API_URL } from "../../config/constants";
import { apiFetch } from "../../utils/apiClient";
import DataState from "../../components/DataState/DataState";
import { toAmount, sumAmounts } from "../../utils/maskedValue";




const CHAT_SAMPLES = [
  {
    question: "Which projects need leadership attention?",
    keywords: ["leadership", "attention", "escalation"],
    answer:
      "Projects marked Red or recently moved from Amber to Red should be reviewed first. Start with accounts that have delayed milestones or invoicing blockers.",
  },
  {
    question: "Show projects with EBITDA below 20%.",
    keywords: ["ebitda", "below 20", "low ebitda", "margin"],
    answer:
      "You can use the dashboard cards to quickly spot low EBITDA projects (displayed in red when under 20%). Prioritize those for scope and cost validation.",
  },
  {
    question: "How can I find projects by business type?",
    keywords: ["business type", "multiple business", "filter"],
    answer:
      "Use the Business Type filter in multi-select mode to choose one or more categories. The dashboard then fetches projects matching all selected business type query values.",
  },
  {
    question: "What is the current invoicing risk?",
    keywords: ["invoice", "invoicing", "payment", "pending"],
    answer:
      "Invoicing risk is usually highest where milestones are complete but invoices are not in Invoiced status. Compare milestone completion against invoice approval and status.",
  },
  {
    question: "How do I check RAG status trends?",
    keywords: ["rag", "red amber green", "trend", "status"],
    answer:
      "Filter by RAG status and review the project cards in sequence. Focus on Red first, then Amber projects that have slipped from Green in recent periods.",
  },
  {
    question: "How should I prioritize project reviews this week?",
    keywords: ["prioritize", "review", "week", "priority"],
    answer:
      "Recommended order: 1) Red RAG projects, 2) EBITDA below target, 3) delayed milestones, 4) invoice aging cases, 5) high contract value projects with delivery risk.",
  },
];

const getSampleAssistantReply = (userQuestion) => {
  const normalizedQuestion = userQuestion.toLowerCase();

  const matchedSample = CHAT_SAMPLES.find((sample) =>
    sample.keywords.some((keyword) => normalizedQuestion.includes(keyword)),
  );

  if (matchedSample) {
    return matchedSample.answer;
  }

  return "I can help with dashboard insights like RAG status, EBITDA, milestones, invoicing, and filters. Try asking about leadership attention, low-EBITDA projects, or business-type filtering.";
};

const SUGGESTED_QUESTIONS = CHAT_SAMPLES.map((sample) => sample.question);

export function Dashboard() {
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    statusFilter: undefined,
    contractCategory: undefined,
    businessArea: undefined,
    dateRangeType: undefined,
    dateRange: undefined,
    clientPartner: undefined,
    deliveryManager: undefined,
    projectManager: undefined,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  const [projects, setProjects] = useState([]);
  const [totalProjects, setTotalProjects] = useState(0);
  const [overview, setOverview] = useState(null);
  const [settledKey, setSettledKey] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [isAssistantTyping, setIsAssistantTyping] = useState(false);
  const chatContainerRef = useRef(null);
  const chatToggleRef = useRef(null);
  const chatBodyRef = useRef(null);
  const pendingRepliesRef = useRef(0);
  const replyTimeoutIdsRef = useRef([]);
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      role: "assistant",
      text: "Hello! I am your AI assistant. Ask me anything about your projects.",
    },
  ]);

  const resetChat = () => {
    replyTimeoutIdsRef.current.forEach((timeoutId) => {
      globalThis.clearTimeout(timeoutId);
    });
    replyTimeoutIdsRef.current = [];
    pendingRepliesRef.current = 0;
    setIsAssistantTyping(false);

    setChatMessages([
      {
        id: 1,
        role: "assistant",
        text: "Hello! I am your AI assistant. Ask me anything about your projects.",
      },
    ]);
    setChatInput("");
  };

  const sendChatMessage = (message) => {
    const trimmed = message.trim();
    if (!trimmed) return;

    const assistantReply = getSampleAssistantReply(trimmed);
    const messageId = Date.now();

    setChatMessages((prev) => [
      ...prev,
      { id: messageId, role: "user", text: trimmed },
    ]);

    pendingRepliesRef.current += 1;
    setIsAssistantTyping(true);

    const simulatedDelay = 900 + Math.floor(Math.random() * 800);
    const timeoutId = globalThis.setTimeout(() => {
      setChatMessages((prev) => [
        ...prev,
        { id: messageId + 1, role: "assistant", text: assistantReply },
      ]);

      pendingRepliesRef.current = Math.max(pendingRepliesRef.current - 1, 0);
      if (pendingRepliesRef.current === 0) {
        setIsAssistantTyping(false);
      }

      replyTimeoutIdsRef.current = replyTimeoutIdsRef.current.filter(
        (id) => id !== timeoutId,
      );
    }, simulatedDelay);

    replyTimeoutIdsRef.current.push(timeoutId);
    setChatInput("");
  };

  const handleSendMessage = () => {
    sendChatMessage(chatInput);
  };

  useEffect(() => {
    if (!isChatOpen || !chatBodyRef.current) {
      return;
    }

    chatBodyRef.current.scrollTo({
      top: chatBodyRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [chatMessages, isAssistantTyping, isChatOpen]);

  useEffect(() => {
    return () => {
      replyTimeoutIdsRef.current.forEach((timeoutId) => {
        globalThis.clearTimeout(timeoutId);
      });
      replyTimeoutIdsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!isChatOpen) {
      return;
    }

    const handleOutsideClick = (event) => {
      const target = event.target;
      const clickedInsideChat = chatContainerRef.current?.contains(target);
      const clickedToggle = chatToggleRef.current?.contains(target);

      if (!clickedInsideChat && !clickedToggle) {
        setIsChatOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isChatOpen]);

  // Request key: changes with every page, size, filter, or reload. The status is 'loading' until the
  // request for this key has settled. It is derived in the same render, so no old rows show meanwhile.
  const requestKey = JSON.stringify([currentPage, pageSize, appliedFilters, reloadKey]);

  useEffect(() => {
    let isCancelled = false;

    const fetchProjects = async () => {
      try {
        setLoadError("");

        const token =
          sessionStorage.getItem("token") ||
          sessionStorage.getItem("authToken") ||
          sessionStorage.getItem("accessToken");

        const tokenType = sessionStorage.getItem("tokenType") || "Bearer";

        if (!token) {
          console.error("Authentication token not found");
          return;
        }

        const normalizedToken = token
          .replace(/^"|"$/g, "")
          .replace(/^Bearer\s+/i, "")
          .trim();

        const requestUrl = new URL(
          BASE_PROJECTS_API_URL,
          globalThis.location.origin,
        );

        requestUrl.searchParams.set("page", String(Math.max(currentPage - 1, 0)));
        requestUrl.searchParams.set("size", String(pageSize));
        requestUrl.searchParams.set("sortBy", "startDate");
        requestUrl.searchParams.set("sortDirection", "asc");

        const keyword = (appliedFilters.search || "").trim();

        if (keyword) {
          requestUrl.searchParams.set("search", keyword);
        }

        if (appliedFilters.statusFilter === "true") {
          requestUrl.searchParams.set("status", "true");
        } else if (appliedFilters.statusFilter === "false") {
          requestUrl.searchParams.set("status", "false");
        }
        if(appliedFilters.ragStatus){
          requestUrl.searchParams.set("ragStatus", appliedFilters.ragStatus);
        }

        if (appliedFilters.contractCategory) {
          requestUrl.searchParams.set(
            "contractCategory",
            appliedFilters.contractCategory,
          );
        }

        const selectedbusinessAreas = [];

        if (Array.isArray(appliedFilters.businessArea)) {
          selectedbusinessAreas.push(...appliedFilters.businessArea);
        } else if (appliedFilters.businessArea) {
          selectedbusinessAreas.push(appliedFilters.businessArea);
        }

        if (selectedbusinessAreas.length) {
          selectedbusinessAreas
            .map((businessArea) => businessArea.trim())
            .filter(Boolean)
            .forEach((businessArea) => {
              requestUrl.searchParams.append("businessArea", businessArea);
            });
        }

        if (appliedFilters.dateRange && appliedFilters.dateRangeType) {
          const [fromRaw, toRaw] = appliedFilters.dateRange.split("|");

          switch (appliedFilters.dateRangeType) {
            case "DATE": {
              const toIsoDate = (value) => {
                if (!value) return "";

                const [month, day, year] = value.split("-");

                if (!month || !day || !year) {
                  return "";
                }

                return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
              };

              const fromDate = toIsoDate(fromRaw);
              const toDate = toIsoDate(toRaw);

              if (fromDate) {
                requestUrl.searchParams.set("fromDate", fromDate);
              }

              if (toDate) {
                requestUrl.searchParams.set("toDate", toDate);
              }

              break;
            }

            case "MONTH": {
              const toMonthRange = (value, isEnd = false) => {
                if (!value) return "";

                const date = new Date(value);

                if (Number.isNaN(date.getTime())) {
                  return "";
                }

                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");

                if (!isEnd) {
                  return `${year}-${month}-01`;
                }

                const lastDay = new Date(
                  year,
                  date.getMonth() + 1,
                  0,
                ).getDate();

                return `${year}-${month}-${String(lastDay).padStart(2, "0")}`;
              };

              const fromDate = toMonthRange(fromRaw);
              const toDate = toMonthRange(toRaw, true);

              if (fromDate) {
                requestUrl.searchParams.set("fromDate", fromDate);
              }

              if (toDate) {
                requestUrl.searchParams.set("toDate", toDate);
              }

              break;
            }

            case "YEAR": {
              const fromYear = fromRaw?.trim();
              const toYear = toRaw?.trim();

              if (fromYear) {
                requestUrl.searchParams.set("fromDate", `${fromYear}-01-01`);
              }

              if (toYear) {
                requestUrl.searchParams.set("toDate", `${toYear}-12-31`);
              }

              break;
            }

            default:
              break;
          }
        }

        if (appliedFilters.clientPartner) {
          requestUrl.searchParams.set(
            "clientPartner",
            appliedFilters.clientPartner,
          );
        }

        if (appliedFilters.deliveryManager) {
          requestUrl.searchParams.set(
            "deliveryManager",
            appliedFilters.deliveryManager,
          );
        }

        if (appliedFilters.projectManager) {
          requestUrl.searchParams.set(
            "projectManager",
            appliedFilters.projectManager,
          );
        }

        console.log("Fetching from:", requestUrl.toString());

        const response = await apiFetch(requestUrl.toString(), {
          method: "GET",
          headers: {
            Authorization: `${tokenType} ${normalizedToken}`,
            "Content-Type": "application/json",
          },
        }, "Failed to fetch projects.");

        if (!response.ok) {
          throw new Error(`Failed to fetch projects: ${response.statusText}`);
        }

        const responseData = await response.json();

        // A newer request has started (filter, page, or reload): drop this stale response.
        if (isCancelled) return;

        console.log("Raw API Response:", responseData);

        if (responseData?.overview) {
          setOverview(responseData.overview );
        }

        const projectsPayload = responseData?.projects;

        const responseArray = Array.isArray(responseData)
          ? responseData
          : Array.isArray(projectsPayload?.projects)
            ? projectsPayload.projects
            : Array.isArray(projectsPayload)
              ? projectsPayload
              : Array.isArray(responseData?.data)
                ? responseData.data
                : [];

        const totalFromApi = Number(
          projectsPayload?.totalRecords ??
            responseData?.totalRecords ??
            responseData?.totalCount ??
            responseData?.totalItems ??
            responseData?.totalElements ??
            0,
        );

        const totalPagesFromApi = Number(
          projectsPayload?.totalPages ?? responseData?.totalPages ?? 0,
        );

        let resolvedTotal = responseArray.length;

        if (totalPagesFromApi > 0) {
          resolvedTotal = totalPagesFromApi * pageSize;
        }

        if (totalFromApi > 0) {
          resolvedTotal = totalFromApi;
        }

        setTotalProjects(resolvedTotal);

        const parseDate = (date) => {
          if (!date) return "";

          if (typeof date === "string") {
            return date.split("T")[0];
          }

          if (typeof date === "object" && date !== null && "$date" in date) {
            const dateStr = (date).$date;
            return new Date(dateStr).toISOString().split("T")[0];
          }

          return "";
        };

        const mappedProjects = responseArray.map((p, idx) => {
          const milestones = Array.isArray(p.sow?.milestones)
  ? p.sow.milestones.map((m) => ({
      id: m.milestoneId || m.id,
      name: m.milestoneName || m.name,
      dueDate: m.endDate || "",
      endDate: m.endDate || "",
      startDate: m.startDate || "",
      completionDate: m.endDate || "",
      paymentAmount: toAmount(m.amount),
      amount: toAmount(m.amount),
      status: m.status || "",
      leadershipStatus:m.leadershipStatus
    }))
  : [];

          const technologies = (p.sow?.technologyRequirements || "")
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);

            const invoices = Array.isArray(p.invoiceDetails)
  ? p.invoiceDetails.map((invoice) => ({
      id: invoice.invoiceId || `INV-${idx}`,
      invoiceId: invoice.invoiceId,
      grossInvoiceAmount: toAmount(invoice.grossInvoiceAmount),
      amount: toAmount(invoice.grossInvoiceAmount),
      approvalStatusInFieldGlass:
        invoice.approvalStatusInFieldGlass || "",
      invoicingStatus: invoice.invoicingStatus || "",
      status:
        invoice.approvalStatusInFieldGlass ||
        invoice.invoicingStatus ||
        invoice.status ||
        "",
      reason: invoice.reason || "",
    }))
  : [];

          const totalInvoiced = sumAmounts(
            invoices
              .filter((invoice) =>
                String(invoice.status || invoice.approvalStatusInFieldGlass)
                  .trim()
                  .toLowerCase() === "invoiced",
              )
              .map((invoice) => invoice.amount),
          );

          const mapped = {
            id: p.projectId || `P-${idx}`,
            projectId: p.projectId || "",
            name: p.projectDescription || p.projectId || "",
            client: p.accountName || p.customerName || "",
            status: p.status === true ? "Active" : "Inactive",
            redFlagged: false,
            projectManager: p.pmName || "",
            dm: p.dm || [],
            pm: p.pm || [],
            startDate: parseDate(p.projectStartDate),
            endDate: parseDate(p.projectEndDate),
            sowId: p.sow?.sowId || "",
            sows: p.sow
              ? [
                  {
                    id: p.sow.sowId,
                    sowNumber: p.sow.sowId,
                    contractValue: toAmount(p.sow.totalContractValue),
                    milestones,
                  },
                ]
              : [],
            invoices,
            technology: technologies,
            tcv: toAmount(p.sow?.totalContractValue),
            contractValue: toAmount(p.sow?.totalContractValue),
            totalContractValue: toAmount(p.sow?.totalContractValue),
            actualRevenue: totalInvoiced,
            plannedRevenue: toAmount(p.sow?.totalContractValue),
            plannedEbitdaPercentage: p.plannedEbitdaPercentage ?? "",
            actualEbitdaPercentage: p.actualEbitdaPercentage ?? "",
            plannedGpmPercentage: p.plannedGpmPercentage ?? "",
            actualGpmPercentage: p.actualGpmPercentage ?? "",
            ragStatus:p.ragStatus??"",
            leadershipStatus:p.leadershipStatus
          };

          console.log(`Project ${idx}:`, {
            rawProject: p,
            mapped,
          });

          return mapped;
        });

        console.log("Mapped projects:", mappedProjects);

        setProjects(mappedProjects);
      } catch (error) {
        console.error("Error fetching projects:", error);
        if (!isCancelled) {
          setLoadError(error instanceof Error ? error.message : "Unable to load projects.");
        }
      } finally {
        if (!isCancelled) {
          setSettledKey(requestKey);
        }
      }
    };

    void fetchProjects();

    return () => {
      isCancelled = true;
    };
  }, [currentPage, pageSize, appliedFilters, reloadKey]);

  const dataStatus = settledKey !== requestKey ? "loading" : loadError ? "error" : "ready";

  return (
    <div style={{ paddingBottom: 60 }}>
      <div style={{ display: dataStatus !== "error" ? "contents" : "none" }}>
        <ProjectFilter
          onFilterChange={(filterData) => {
            setAppliedFilters(filterData);
            setCurrentPage(1);
          }}
        />
      </div>
      <DataState
        status={dataStatus}
        errorTitle="Couldn't load projects"
        error={loadError}
        onRetry={() => setReloadKey((key) => key + 1)}
        skeletonCards={4}
      >
      <>



      <StatusCards overview={overview} />

      <Divider />

      {projects.length === 0 ? (
        <Empty
          description="No projects found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <>
          <Row gutter={[12, 12]} justify="start" align="stretch">
            {projects.map((project) => (
              <Col
                key={project.id}
                xs={24}
                sm={24}
                md={12}
                lg={8}
                xl={8}
                xxl={6}
              >
                <ProjectCard project={project} />
              </Col>
            ))}
          </Row>

          <PillPagination
            currentPage={currentPage}
            pageSize={pageSize}
            total={totalProjects}
            itemLabel="Projects"
            onPageChange={setCurrentPage}
            pageSizeOptions={[12, 24, 36, 48]}         
            onPageSizeChange={setPageSize}
          />
        </>
      )}

      {isChatOpen ? (
        <div ref={chatContainerRef}>
        <Card
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ffffff",
                  background:
                    "linear-gradient(135deg, #3c3c3e 0%, #1d4ed8 55%, #06b6d4 100%)",
                }}
              >
                <SmartToyOutlinedIcon fontSize="small" />
              </div>

              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Watch Tower Smart Assistant</span>
                <span style={{ fontSize: 11, color: "#64748b" }}>
                  Project insights assistant
                </span>
              </div>
            </div>
          }
          extra={
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Tooltip title="New chat">
                <Button
                  type="text"
                  size="small"
                  icon={<AddCommentOutlinedIcon fontSize="small" />}
                  onClick={resetChat}
                />
              </Tooltip>
              <Button style={{ width: "auto" }} type="text" size="small" onClick={() => setIsChatOpen(false)}>
                Close
              </Button>
            </div>
          }
          style={{
            position: "fixed",
            right: 24,
            bottom: 90,
            width: "min(380px, calc(100vw - 24px))",
            height: "min(540px, calc(100vh - 120px))",
            zIndex: 1100,
            borderRadius: 14,
            border: "1px solid #e2e8f0",
            boxShadow: "0 24px 48px rgba(2, 6, 23, 0.2)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            background:
              "linear-gradient(180deg, rgba(248,250,252,0.95) 0%, rgba(255,255,255,1) 30%)",
          }}
          styles={{
            header: {
              borderBottom: "1px solid #e2e8f0",
              background: "rgba(255,255,255,0.9)",
            },
            body: {
              padding: 0,
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <div
            ref={chatBodyRef}
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              padding: "14px 14px 8px",
            }}
          >
            {chatMessages.map((message) => (
              <div
                key={message.id}
                style={{
                  alignSelf: message.role === "user" ? "flex-end" : "flex-start",
                  background:
                    message.role === "user"
                      ? "linear-gradient(135deg, #3c3c3e 0%, #1e293b 100%)"
                      : "#f8fafc",
                  border:
                    message.role === "assistant" ? "1px solid #e2e8f0" : "1px solid #3c3c3e",
                  color: message.role === "user" ? "#f8fafc" : "#3c3c3e",
                  padding: "10px 12px",
                  borderRadius: message.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                  maxWidth: "90%",
                  fontSize: 13,
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                }}
              >
                {message.text}
              </div>
            ))}

            {isAssistantTyping ? (
              <div
                style={{
                  alignSelf: "flex-start",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  color: "#475569",
                  padding: "10px 12px",
                  borderRadius: "14px 14px 14px 4px",
                  maxWidth: "90%",
                  fontSize: 12,
                  lineHeight: 1.4,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Spin size="small" />
                <span>Watch Tower Smart Assistant is typing...</span>
              </div>
            ) : null}
          </div>

          <div
            style={{
              borderTop: "1px solid #e2e8f0",
              background: "#ffffff",
              padding: "10px 12px 12px",
            }}
          >
            <div
              style={{
              display: "flex",
              overflowX: "auto",
              gap: 6,
              marginBottom: 10,
              paddingBottom: 4,
            }}
          >
            {SUGGESTED_QUESTIONS.map((question) => (
              <Button
                key={question}
                size="small"
                type="text"
                onClick={() => sendChatMessage(question)}
                style={{
                  borderRadius: 9999,
                  width: "auto",
                  fontSize: 11,
                  height: "auto",
                  padding: "6px 12px",
                  whiteSpace: "nowrap",
                  textAlign: "left",
                  border: "1px solid #dbeafe",
                  background: "#eff6ff",
                  color: "#1e3a8a",
                }}
              >
                {question}
              </Button>
            ))}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <Input
                placeholder="Message Watch Tower Smart Assistant"
                value={chatInput}
                onChange={(event) => setChatInput(event.target.value)}
                onPressEnter={handleSendMessage}
                style={{
                  borderRadius: 10,
                  borderColor: "#cbd5e1",
                  fontSize: 13,
                }}
              />
              <Button
                type="primary"
                onClick={handleSendMessage}
                icon={<SendRoundedIcon fontSize="small" />}
                style={{
                  borderRadius: 10,
                  minWidth: 42,
                  paddingInline: 10,
                }}
              />
            </div>
          </div>
        </Card>
        </div>
      ) : null}

      <div ref={chatToggleRef}>
      <Tooltip title="Watch Tower Smart Assistant" placement="left">
        <Button
          type="primary"
          shape="circle"
          icon={<ChatBubbleIcon style={{ fontSize: 20 }} />}
          onClick={() => setIsChatOpen((prev) => !prev)}
          style={{
            position: "fixed",
            right: 24,
            bottom: 24,
            width: 56,
            height: 56,
            borderRadius: "50%",
            zIndex: 1100,
            background:
              "linear-gradient(135deg, #3c3c3e 0%, #1d4ed8 60%, #06b6d4 100%)",
            boxShadow: "0 12px 28px rgba(15, 23, 42, 0.35)",
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      </Tooltip>
      </div>
      </>
      </DataState>
    </div>
  );
}